import {
  AccountType,
  API_BORROWER_ACTIVITIES,
  API_USER_ACTIVITIES,
  API_USER_ACTIVITIES_ALL,
  API_USER_ACTIVITIES_BY_TYPE,
  API_USER_ACTIVITIES_DOWNLOAD_ALL_CSV,
  API_USER_ACTIVITIES_DOWNLOAD_CSV,
  API_USER_ACTIVITIES_DOWNLOAD_PDF,
  API_USER_ACTIVITY_FILTER,
  API_USER_ADMIN_EMAILS,
  API_USER_ADMIN_TO_AE_EMAILS,
  API_USER_AUTHORIZED_SIGNORS,
  API_USER_AUTO_COMPLETE,
  API_USER_BROKER_PAYMENT,
  API_USER_BROKER_VERIFICATION,
  API_USER_BROKER_VERIFICATION_BY_USER,
  API_USER_BULK_UPDATE_CAN_LOGIN,
  API_USER_BULK_UPDATE_STATUS,
  API_USER_BY_COMPANY,
  API_USER_BY_ID,
  API_USER_CHILD_BROKER,
  API_USER_CHILDREN,
  API_USER_CONFIGURATION,
  API_USER_COUNTERPARTY_APPROVAL_DOCUMENTS,
  API_USER_CREATE,
  API_USER_DELETE,
  API_USER_DOWNLOAD_COUNTERPARTY_APPROVAL_DOCUMENT,
  API_USER_EMAIL_TEMPLATE,
  API_USER_EMAIL_TEMPLATES,
  API_USER_EMAILS,
  API_USER_EXPORT_HISTORY,
  API_USER_FILTER,
  API_USER_FILTER_MASTER_EMAILS,
  API_USER_FILTER_ROLES,
  API_USER_GET_ALL_FIELD_LOGS,
  API_USER_GET_LOGS,
  API_USER_GROUP,
  API_USER_GROUP_BY_ID,
  API_USER_INTERNAL_EMAILS,
  API_USER_INTERNAL_USERS,
  API_USER_LOGIN_PAGE_MODALS,
  API_USER_LOGIN_SHARING,
  API_USER_MENTION_GROUPS,
  API_USER_MULTIPLE,
  API_USER_NOTES,
  API_USER_PROFILE,
  API_USER_PROPS,
  API_USER_RECOVER,
  API_USER_ROLES,
  API_USER_SIGN_BROKER_VERIFICATION_PDF,
  API_USER_TREE,
  API_USER_UNSUBSCRIBE,
  API_USER_UPDATE,
  API_USER_UPDATE_AGREEMENT_TEMPLATE,
  API_USER_UPDATE_CAN_LOGIN,
  API_USER_UPDATE_PWD,
  API_USER_UPDATE_STATUS,
  API_USER_WHITE_LABEL,
  API_USER_WHITE_LABEL_CUSTOM_DNS,
  baseUrl,
  UserActivityFilterQuery,
  UserFilterQuery,
} from 'config'
import type { AuthorizedSignor, ChildBroker } from 'pages/ManageAccounts'
import { store } from 'reducers'
import Api from 'services/api'

import { downloadFile } from './documents'

const API_USER_ACCOUNT_EXECUTIVES = '/auth/getAccountExecutives'

export interface IUserGroup {
  id: number
  name: string
  emails: string[]
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export const requestUserRoles = (accountType: AccountType, userId = 0): Promise<Record<number, string>> => {
  return Api.get(API_USER_ROLES, { accountType, userId })
}

export const getAccountExecutives = (): Promise<Record<number, string>> => {
  return Api.get(API_USER_ACCOUNT_EXECUTIVES)
}

export const requestUserProps = (userId: number): Promise<Record<string, string>> => {
  return Api.get(API_USER_PROPS, {}, { userId })
}

export const submitUser = (userId: number, data: Record<string, string | number | boolean>) => {
  if (!userId) return Api.uploadFiles(API_USER_CREATE, data, {}, [])

  return Api.uploadFiles(API_USER_UPDATE, data, { userId }, [])
}

export const userSearchAutoComplete = (data: any) => {
  return Api.post(API_USER_AUTO_COMPLETE, data)
}

export const filterUsers = (filterQuery: UserFilterQuery = {}) => {
  return Api.get(API_USER_FILTER, filterQuery)
}

export const getExportHistory = (skip: number, count: number) => {
  return Api.get(API_USER_EXPORT_HISTORY, { skip, count })
}

export const getUserById = (id: number) => {
  return Api.get(API_USER_BY_ID, {}, { id })
}

export const filterUserRoles = (query: string) => {
  return Api.get(API_USER_FILTER_ROLES, { query })
}

export const getUserConfiguration = (userId: number) => {
  return Api.get(API_USER_CONFIGURATION, {}, { userId })
}

export const updateUserConfiguration = (userId: number, data: Record<string, string | number | boolean> | any) => {
  return Api.post(API_USER_CONFIGURATION, data, { userId })
}

export const updateUserCanLogin = (userId: number, canLogin: boolean) => {
  return Api.put(API_USER_UPDATE_CAN_LOGIN, { canLogin }, { userId })
}

export const updateUserStatus = (userId: number, status: boolean) => {
  return Api.put(API_USER_UPDATE_STATUS, { status }, { userId })
}

export const bulkUpdateUserStatus = (status: boolean) => {
  return Api.put(API_USER_BULK_UPDATE_STATUS, { status })
}

export const bulkUpdateUserCanLogin = (canLogin: boolean) => {
  return Api.put(API_USER_BULK_UPDATE_CAN_LOGIN, { canLogin })
}

export const deleteUser = (userId: number) => {
  return Api.delete(API_USER_DELETE, {}, { userId })
}

export const recoverUser = (userId: number) => {
  return Api.put(API_USER_RECOVER, {}, { userId })
}

export const updatePassword = (email: string, password: string, oldPassword?: string) => {
  return Api.put(API_USER_UPDATE_PWD, { email, password, oldPassword })
}

export const getChildUsers = (userId: number) => {
  return Api.get(API_USER_CHILDREN, {}, { userId })
}

export const updateChildUserTypes = (userId: number, values: Record<string, string>) => {
  return Api.put(API_USER_CHILDREN, { values }, { userId })
}

export const updateChildLoanUsers = (userId: number, values: Record<string, string>) => {
  return Api.put(`${API_USER_CHILDREN}/loan`, { values }, { userId })
}

export const updateUserAccountExecutive = (userIds: number[], accountExecutive: number) => {
  return Api.put(API_USER_MULTIPLE, { userIds, data: { accountExecutive } })
}

export const getUserTree = () => {
  return Api.get(API_USER_TREE, {}, {})
}

export const getUserLogs = (userId: number, fieldName: string) => {
  return Api.get(API_USER_GET_LOGS, {}, { userId, fieldName })
}

export const getUserAllFieldLogs = (fieldName: string) => {
  return Api.get(API_USER_GET_ALL_FIELD_LOGS, {}, { fieldName })
}

export const getUserByCompany = (companyName: string) => {
  return Api.get(API_USER_BY_COMPANY, { companyName })
}

export const updateProfile = (data: Record<string, string | number | boolean>) => {
  return Api.put(API_USER_PROFILE, data)
}

export interface WhiteLabelInfo {
  logo: string
  logoUrl: string
  footer: Record<string, any>
}

export const getWhiteLabelInfo = (): Promise<WhiteLabelInfo> => {
  return Api.get(API_USER_WHITE_LABEL)
}

export const updateWhiteLabelInfo = (data: Record<string, any>): Promise<WhiteLabelInfo> => {
  return Api.uploadFiles(API_USER_WHITE_LABEL, data, {}, [])
}

export const filterUserActivity = (filterQuery: UserActivityFilterQuery = {}): Promise<Record<string, any>[]> => {
  return Api.get(API_USER_ACTIVITY_FILTER, filterQuery)
}

export const filterUserActivities = (id: number, filter: any = {}) => {
  return Api.get(API_USER_ACTIVITIES, filter, { userId: id })
}

export const filterAllUserActivities = (filter: any = {}) => {
  return Api.get(API_USER_ACTIVITIES_ALL, filter)
}

export const filterUserActivitiesByType = (filter: any = {}) => {
  return Api.get(API_USER_ACTIVITIES_BY_TYPE, filter)
}

export const filterBorrowerActivities = (id: number, filter: any = {}) => {
  return Api.get(API_BORROWER_ACTIVITIES, filter, { userId: id })
}

export const downloadCSVfilterUserActivities = async (id: number | undefined, filter: any = {}) => {
  const res: Blob = await Api.get(API_USER_ACTIVITIES_DOWNLOAD_CSV, filter, { userId: id }, { responseType: 'blob' })
  downloadFile(`User Activity.csv`, res)
}

export const downloadPDFfilterUserActivities = async (id: number | undefined, filter: any = {}) => {
  const res: Blob = await Api.get(API_USER_ACTIVITIES_DOWNLOAD_PDF, filter, { userId: id }, { responseType: 'blob' })
  downloadFile(`User Activity.pdf`, res)
}

export const downloadAllUserActivitiesCSV = async (filter: {
  startDate?: string
  endDate?: string
  email?: string
  accountType?: string
  excludeUserIds?: string
  excludeAccountTypes?: string
}) => {
  const res: Blob = await Api.get(API_USER_ACTIVITIES_DOWNLOAD_ALL_CSV, filter, {}, { responseType: 'blob' })
  downloadFile(`All User Activity.csv`, res)
}

export const getAdminEmails = (): Promise<string[]> => {
  return Api.get(API_USER_ADMIN_EMAILS)
}

export const getAdminToAeEmails = (): Promise<string[]> => {
  return Api.get(API_USER_ADMIN_TO_AE_EMAILS)
}

export const filterMasterEmails = (emails: string[]) => {
  return Api.post(API_USER_FILTER_MASTER_EMAILS, { emails })
}

export const updateChildBroker = (
  userId: number,
  children: { email: string; name?: string }[],
): Promise<ChildBroker[]> => {
  return Api.put(API_USER_CHILD_BROKER, { children }, { userId })
}

export const updateAuthorizedSignors = (userId: number, signors: AuthorizedSignor[]): Promise<AuthorizedSignor[]> => {
  return Api.put(API_USER_AUTHORIZED_SIGNORS, { signors }, { userId })
}

export const updateWhiteLabelCustomDns = (
  userId: number,
  data: { slug?: string; fromLocalPart?: string; fromDns?: string },
) => {
  return Api.put(API_USER_WHITE_LABEL_CUSTOM_DNS, data, { userId })
}

export const updateAgreementTemplate = (userId: number, template: Record<string, any>): Promise<ChildBroker[]> => {
  return Api.put(API_USER_UPDATE_AGREEMENT_TEMPLATE, { template }, { userId })
}

export const submitBrokerVerification = (data: any[]) => {
  return Api.post(API_USER_BROKER_VERIFICATION, data)
}

export const getBrokerVerification = () => {
  return Api.get(API_USER_BROKER_VERIFICATION)
}

export const getBrokerVerificationByUser = (userId: number) => {
  return Api.get(API_USER_BROKER_VERIFICATION_BY_USER, {}, { userId })
}

export const sendCounterApprovalPaymentData = (userId: number, data: Record<string, any>) => {
  return Api.post(API_USER_BROKER_PAYMENT, data, { userId })
}

export const signBrokerVerificationPdfForUser = (signorId: number) => {
  return Api.post(API_USER_SIGN_BROKER_VERIFICATION_PDF, { signorId }, {}, { timeout: 150000 })
}

export const downloadBrokerAgreementPdf = (userId: number, template?: string) => {
  const { token } = store.getState().auth
  let url = `${baseUrl}/user/downloadBrokerAgreementPdf/${userId}${template ? '/' + template : ''}?token=${token}`
  window.location.href = url
}

export const getCounterpartyApprovalDocuments = (userId: number): Promise<{ documents: { name: string }[] }> => {
  return Api.get(API_USER_COUNTERPARTY_APPROVAL_DOCUMENTS, {}, { userId })
}

export const downloadCounterpartyApprovalDocument = async (userId: number, name: string) => {
  const response: Blob = await Api.post(
    API_USER_DOWNLOAD_COUNTERPARTY_APPROVAL_DOCUMENT,
    { name },
    { userId },
    { responseType: 'blob' },
  )
  downloadFile(`${name}.pdf`, response)
}

export const getUserNotes = (userId: number, isBorrower: boolean) => {
  return Api.get(API_USER_NOTES, {}, { userId, isBorrower })
}

export const createUserNote = (userId: number, data: Record<string, any>) => {
  return Api.post(API_USER_NOTES, data, { userId, isBorrower: data.isBorrower })
}

export const unsubscribeEmail = (data: any) => {
  return Api.post(API_USER_UNSUBSCRIBE, data, {})
}

export const getLoginPageModal = () => {
  return Api.get(API_USER_LOGIN_PAGE_MODALS)
}

export const getLoanSharingUsers = () => {
  return Api.get(API_USER_LOGIN_SHARING)
}

export const getInternalUserEmails = (): Promise<string[]> => {
  return Api.get(API_USER_INTERNAL_EMAILS)
}

export const getInternalUsers = (): Promise<{ name: string; email: string; accountType: string }[]> => {
  return Api.get(API_USER_INTERNAL_USERS)
}

/** Departments taggable in a note - the tenant's own internal account types, with their headcount. */
export const getMentionGroups = (): Promise<{ key: string; title: string; count: number }[]> => {
  return Api.get(API_USER_MENTION_GROUPS)
}

export const getUserGroups = () => {
  return Api.get(API_USER_GROUP)
}

export const getUserEmailsByAccountType = (accountType: string) => {
  return Api.get(API_USER_EMAILS, { accountType })
}

export const getUserGroupById = (id: number) => {
  return Api.get(API_USER_GROUP_BY_ID, {}, { id })
}

export const createUserGroup = (data: { name: string; emails?: string[] }) => {
  return Api.post(API_USER_GROUP, data)
}

export const updateUserGroup = (id: number, data: { name: string; emails?: string[] }) => {
  return Api.put(API_USER_GROUP_BY_ID, data, { id })
}

export const deleteUserGroup = (id: number) => {
  return Api.delete(API_USER_GROUP_BY_ID, {}, { id })
}

export interface UserEmailTemplate {
  id: number
  baseTemplateId: number
  isCustomized: boolean
  overrideId: number | null
  hasTeamOverride?: boolean
  group: string
  purpose?: string[]
  from: string
  to: string[]
  cc: string[]
  subject: string
  title: string
  elements: string[]
  links: any[]
  content: string
  automations?: any[]
}

export const getUserEmailTemplates = (userId: number): Promise<UserEmailTemplate[]> => {
  return Api.get(API_USER_EMAIL_TEMPLATES, {}, { userId })
}

export const upsertUserEmailTemplate = (
  userId: number,
  baseTemplateId: number,
  data: Record<string, any>,
): Promise<UserEmailTemplate> => {
  return Api.put(API_USER_EMAIL_TEMPLATE, data, { userId, baseTemplateId })
}

export const resetUserEmailTemplate = (userId: number, baseTemplateId: number): Promise<UserEmailTemplate> => {
  return Api.delete(API_USER_EMAIL_TEMPLATE, {}, { userId, baseTemplateId })
}
